import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const TABS = [
  { id: 'smile', label: 'Smile' },
  { id: 'rest', label: 'Rest' },
  { id: 'position', label: 'Position' },
  { id: 'horizontal', label: 'Horizontal Position' },
];

const TAB_CONTENT = {
  smile: {
    title: 'Smile Analysis',
    subtitle: 'Evaluating tooth display during a full smile',
    leftContent: {
      title: 'The Goal',
      description:
        'During a full smile, the maxillary incisal edges should follow the curvature of the lower lip (the smile arc). The ideal smile displays the full clinical crown of the maxillary central incisors with 1-2mm of gingival tissue visible.',
      rules: [
        'Full crown display of upper centrals',
        'Smile arc follows lower lip curvature',
        '1-2mm gingival display is ideal',
        'Consonant smile arc (not flat or reverse)',
      ],
    },
    diagramLabel: 'Full Smile — Lip-to-Tooth Relationship',
    guidanceText:
      'Photograph the patient in a natural, posed smile. Evaluate the smile arc, tooth display, and gingival exposure. A flat or reverse smile arc may indicate vertical maxillary excess or over-eruption of posterior teeth.',
  },
  rest: {
    title: 'Rest Position Analysis',
    subtitle: 'Evaluating tooth display at rest (lips apart)',
    leftContent: {
      title: 'The Goal',
      description:
        'At rest position (lips slightly apart), the maxillary incisal edges should be visible by approximately 2-4mm in younger patients. Tooth display at rest naturally decreases with age as soft tissues lose elasticity.',
      rules: [
        'Young adults: 3-4mm of upper incisor display',
        'Middle age: 1-2mm of upper incisor display',
        'Older adults: 0mm upper / lower incisors may show',
        'Loss of display may indicate need for lengthening',
      ],
    },
    diagramLabel: 'Rest Position — Age-Related Changes',
    guidanceText:
      'Have the patient relax their lips in a natural rest position. Measure the amount of maxillary incisor visible below the upper lip. This measurement is critical for planning crown lengthening, veneers, or orthodontic intrusion/extrusion.',
  },
  position: {
    title: 'Vertical Incisal Edge Position',
    subtitle: 'Determining the ideal vertical position of the incisal edge',
    leftContent: {
      title: 'The Goal',
      description:
        'The maxillary incisal edge position is the most important starting point in treatment planning. The vertical position determines smile aesthetics, phonetics (F and V sounds), and anterior guidance. The incisal edge should be at approximately 50% of the distance between the nose and chin.',
      rules: [
        'Incisal edge at ~50% of lower facial third',
        'Younger patients: slightly longer teeth are acceptable',
        'Older patients: consider that teeth wear with age',
        'F/V sounds require light contact of incisal edge on lower lip',
      ],
    },
    diagramLabel: 'Vertical Position — Lower Face Proportions',
    guidanceText:
      'Measure from subnasale to soft tissue menton. The upper incisal edge should be positioned at approximately the midpoint. Use the closest F-sound phonetics test: have the patient say "fifty-five" and observe where the incisal edges contact the lower lip vermillion border.',
  },
  horizontal: {
    title: 'Horizontal Incisal Edge Position',
    subtitle: 'AP position of the incisal edge relative to the face',
    leftContent: {
      title: 'The Goal',
      description:
        'The horizontal (anteroposterior) position of the maxillary incisal edge affects both aesthetics and function. The incisal edge should be positioned to provide proper overjet (2-3mm) and support the upper lip. Too far forward creates a "toothy" appearance; too far lingual collapses lip support.',
      rules: [
        'Normal overjet: 2-3mm',
        'Incisal edge should support the upper lip',
        'AP position affects profile aesthetics',
        'Consider cephalometric norms (SNA, SNB, ANB)',
      ],
    },
    diagramLabel: 'Horizontal Position — Overjet & Lip Support',
    guidanceText:
      'Evaluate the AP position by viewing the patient from the lateral (profile) view. The incisal edge should be visible in profile, supporting the upper lip without excessive protrusion. Use a ruler or digital analysis to measure overjet from the labial surface of the lower incisor.',
  },
};

const LipDiagram = ({ tab }) => {
  const isPosition = tab === 'position';
  const isHorizontal = tab === 'horizontal';

  return (
    <svg viewBox="0 0 200 260" className="w-full max-w-sm mx-auto">
      {/* Face outline - simplified lower face */}
      <ellipse cx="100" cy="130" rx="80" ry="120" fill="none" stroke="#e5e7eb" strokeWidth="1" />

      {/* Nose */}
      <path d="M 90 60 Q 100 55 110 60" fill="none" stroke="#d1d5db" strokeWidth="1.5" />
      <line x1="100" y1="55" x2="100" y2="70" stroke="#d1d5db" strokeWidth="1" />

      {/* Upper lip */}
      <path
        d="M 65 110 Q 80 100 95 108 Q 100 105 105 108 Q 120 100 135 110"
        fill="none"
        stroke="#6b7280"
        strokeWidth="2"
      />

      {/* Lower lip */}
      <path
        d="M 65 110 Q 75 130 100 135 Q 125 130 135 110"
        fill="none"
        stroke="#6b7280"
        strokeWidth="2"
      />

      {/* Teeth visible between lips */}
      <rect x="80" y="110" width="40" height="12" rx="2" fill="#f9fafb" stroke="#d1d5db" strokeWidth="1" />
      {/* Tooth dividers */}
      <line x1="90" y1="110" x2="90" y2="122" stroke="#e5e7eb" strokeWidth="0.5" />
      <line x1="100" y1="110" x2="100" y2="122" stroke="#e5e7eb" strokeWidth="0.5" />
      <line x1="110" y1="110" x2="110" y2="122" stroke="#e5e7eb" strokeWidth="0.5" />

      {/* Chin */}
      <path d="M 80 170 Q 100 185 120 170" fill="none" stroke="#d1d5db" strokeWidth="1" />

      {isPosition && (
        <>
          {/* 50% horizontal marker line */}
          <line x1="30" y1="122" x2="170" y2="122" stroke="#3b82f6" strokeWidth="1" strokeDasharray="4 3" />
          <text x="172" y="125" fontSize="8" fill="#3b82f6" fontFamily="Inter, sans-serif">50%</text>

          {/* Vertical measurement lines */}
          <line x1="25" y1="60" x2="25" y2="180" stroke="#9ca3af" strokeWidth="0.5" />
          <line x1="22" y1="60" x2="28" y2="60" stroke="#9ca3af" strokeWidth="1" />
          <line x1="22" y1="180" x2="28" y2="180" stroke="#9ca3af" strokeWidth="1" />
          <text x="15" y="125" fontSize="7" fill="#9ca3af" textAnchor="middle" transform="rotate(-90, 15, 125)" fontFamily="Inter, sans-serif">
            Lower 1/3
          </text>
        </>
      )}

      {isHorizontal && (
        <>
          {/* Overjet measurement */}
          <line x1="100" y1="122" x2="100" y2="145" stroke="#ef4444" strokeWidth="1" strokeDasharray="3 2" />
          <line x1="100" y1="140" x2="112" y2="140" stroke="#3b82f6" strokeWidth="1.5" />
          <text x="108" y="150" fontSize="7" fill="#3b82f6" fontFamily="Inter, sans-serif">2-3mm</text>

          {/* AP arrow */}
          <line x1="85" y1="95" x2="115" y2="95" stroke="#ef4444" strokeWidth="1" markerEnd="url(#arrowRedSmall)" />
          <defs>
            <marker id="arrowRedSmall" markerWidth="5" markerHeight="3.5" refX="4" refY="1.75" orient="auto">
              <polygon points="0 0, 5 1.75, 0 3.5" fill="#ef4444" />
            </marker>
          </defs>
          <text x="100" y="90" fontSize="7" fill="#ef4444" textAnchor="middle" fontFamily="Inter, sans-serif">AP position</text>
        </>
      )}

      {!isPosition && !isHorizontal && (
        <>
          {/* Smile arc curve */}
          <path
            d="M 70 122 Q 100 132 130 122"
            fill="none"
            stroke="#3b82f6"
            strokeWidth="1.5"
            strokeDasharray="4 3"
          />
          <text x="100" y="142" fontSize="7" fill="#3b82f6" textAnchor="middle" fontFamily="Inter, sans-serif">
            {tab === 'smile' ? 'Smile arc' : 'Rest display'}
          </text>
        </>
      )}
    </svg>
  );
};

const MaxillaryIncisalEdge = () => {
  const [activeTab, setActiveTab] = useState('position');
  const content = TAB_CONTENT[activeTab];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Back link */}
      <Link
        to="/2d-checks"
        className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 mb-4"
      >
        <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to 2D Checks
      </Link>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Maxillary Incisal Edge Position</h1>
        <p className="text-gray-500 mt-1">Evaluate vertical and horizontal positioning</p>
      </div>

      {/* Tab pills */}
      <div className="flex items-center space-x-2 mb-6 flex-wrap gap-y-2">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-150 whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Title bar */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900">{content.title}</h2>
          <p className="text-sm text-gray-500 mt-0.5">{content.subtitle}</p>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
          {/* Left column: text content */}
          <div className="px-6 py-6 border-b lg:border-b-0 lg:border-r border-gray-100">
            <h3 className="text-base font-semibold text-gray-800 mb-3">
              {content.leftContent.title}
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed mb-4">
              {content.leftContent.description}
            </p>
            <ul className="space-y-2">
              {content.leftContent.rules.map((rule, i) => (
                <li key={i} className="flex items-start text-sm text-gray-600">
                  <svg className="w-4 h-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {rule}
                </li>
              ))}
            </ul>
          </div>

          {/* Right column: diagram */}
          <div className="px-6 py-6 flex flex-col items-center justify-center bg-gray-50/50">
            <LipDiagram tab={activeTab} />
            <p className="text-xs text-gray-500 mt-2 text-center">{content.diagramLabel}</p>
          </div>
        </div>

        {/* Clinical guidance */}
        <div className="px-6 py-4 bg-blue-50 border-t border-blue-100">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-blue-800">Clinical Question</h3>
              <p className="text-sm text-blue-700 mt-1 leading-relaxed">{content.guidanceText}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaxillaryIncisalEdge;
